// The calls the lot cannot answer today.
//
// Far more people ring than there are cars, and the valuable calls are the
// ones with no car behind them: "do you have a Swift?" — no — and a Swift
// arrives three weeks later with nobody remembering who asked. So every call
// is written down, and the moment a matching car lands the caller comes back
// to the top of this screen with a Call back button next to their number.
//
// Two shapes, one record:
//   · WAITING  — they want a car we don't have. Matched on brand/model.
//   · ON A CAR — they asked about a car on the lot; it rides on that card.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiCheck,
  FiPhone,
  FiPhoneCall,
  FiPlus,
  FiRefreshCw,
  FiRotateCcw,
  FiSearch,
  FiTrash2,
  FiUser,
} from 'react-icons/fi';
import { Alert, Button } from '../common/index.js';
import {
  CarPicker, Field, Modal, NumberInput, Select, errText, fmtMoney,
  inputCls, searchIconCls, searchInputCls,
} from './controls.jsx';
import { carsTeamApi } from '../../api/carsApi';

const STATUS_TABS = [
  { value: 'open', label: 'Waiting' },
  { value: 'contacted', label: 'Called back' },
  { value: 'closed', label: 'Closed' },
  { value: '', label: 'All' },
];

// Where the caller came from — asked on every call, because it is the only
// way to learn which of OLX, Instagram and word of mouth actually sells cars.
const SOURCE_OPTIONS = [
  { value: 'olx', label: 'OLX' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'reference', label: 'Reference / friend' },
  { value: 'walkin', label: 'Walked in' },
  { value: 'website', label: 'Website' },
  { value: 'google', label: 'Google' },
  { value: 'other', label: 'Other' },
];

const SOURCE_LABELS = Object.fromEntries(SOURCE_OPTIONS.map((s) => [s.value, s.label]));

const STATUS_TONE = {
  open: 'bg-amber-50 text-amber-700 ring-amber-200',
  contacted: 'bg-sky-50 text-sky-700 ring-sky-200',
  closed: 'bg-slate-100 text-slate-500 ring-slate-200',
};

const Chip = ({ tone = 'slate', children }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${
    STATUS_TONE[tone] || 'bg-slate-100 text-slate-600 ring-slate-200'
  }`}>
    {children}
  </span>
);

// ── Write a call down ────────────────────────────────────────────────────────
// `car` set means the customer asked about that car — the form drops the
// brand/model picker, because the car IS the answer to what they want.
export const InquiryForm = ({ open, onClose, onSaved, catalog = [], car = null, initial = null }) => {
  const editing = Boolean(initial);
  const blank = {
    customer_name: '', customer_phone: '', source: '', budget_max: '',
    brand: '', model: '', year_from: '', note: '', pinned: false,
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm(initial ? {
      customer_name: initial.customer_name || '',
      customer_phone: initial.customer_phone || '',
      source: initial.source || '',
      budget_max: initial.budget_max != null ? String(Number(initial.budget_max)) : '',
      brand: initial.brand || '',
      model: initial.model || '',
      year_from: initial.year_from || '',
      note: initial.note || '',
      pinned: Boolean(initial.pinned),
    } : {
      customer_name: '', customer_phone: '', source: '', budget_max: '',
      brand: '', model: '', year_from: '', note: '', pinned: false,
    });
  }, [open, initial]);

  const submit = async () => {
    setError('');
    if (!form.customer_name.trim()) { setError('Whose call is this? Enter their name.'); return; }
    const digits = form.customer_phone.replace(/\D/g, '');
    if (digits.length < 10) {
      setError('A number you cannot ring back is not worth writing down — enter 10 digits.');
      return;
    }
    if (!editing && !form.source) {
      setError('How did they hear about CAP Cars? OLX, Instagram, a friend…');
      return;
    }
    // Budget is enough — that is what most callers actually give. A named
    // car narrows it further; neither means there is nothing to watch for.
    if (!car && !form.budget_max && !form.brand.trim()) {
      setError('Note their budget at least — or which car they want.');
      return;
    }
    setBusy(true);
    try {
      const body = {
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        source: form.source,
        note: form.note.trim(),
        pinned: form.pinned,
        year_from: form.year_from,
        budget_max: form.budget_max === '' ? null : form.budget_max,
        ...(car ? { car: car.id, brand: '', model: '' }
          : { brand: form.brand.trim(), model: form.model.trim() }),
      };
      if (editing) await carsTeamApi.updateInquiry(initial.id, body);
      else await carsTeamApi.addInquiry(body);
      onSaved();
      onClose();
    } catch (e) {
      setError(errText(e, 'Could not save this enquiry.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit this enquiry' : (car ? 'Someone asked about this car' : 'Someone wants a car')}
      subtitle={car
        ? `${car.display_name} — note what they said so the next call is not a fresh start.`
        : 'We ring them back the day a car like this arrives.'}
    >
      {error && <div className="mb-4"><Alert type="error" message={error} /></div>}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Customer name" required>
          <input className={inputCls} value={form.customer_name}
            onChange={(e) => set('customer_name')(e.target.value)} placeholder="Who called" />
        </Field>
        <Field label="Phone" required hint="How we ring them back">
          <input className={inputCls} value={form.customer_phone}
            onChange={(e) => set('customer_phone')(e.target.value)}
            placeholder="10-digit mobile number" />
        </Field>
      </div>

      <div className="mt-3">
        <Field label="How did they hear about us?" required>
          <Select
            value={form.source}
            onChange={set('source')}
            options={SOURCE_OPTIONS}
            placeholder="OLX, Instagram, a friend…"
          />
        </Field>
      </div>

      {!car && (
        <>
          {/* Budget FIRST — that is what a caller actually says. A specific
              brand or model is the rarer call, so it comes second. */}
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Budget up to" required
              hint="What they said they'll spend — enough on its own">
              <NumberInput value={form.budget_max} onChange={set('budget_max')}
                placeholder="e.g. 600000" />
            </Field>
            <Field label="Year from" hint="Optional">
              <input className={inputCls} value={form.year_from}
                onChange={(e) => set('year_from')(e.target.value)} placeholder="e.g. 2018" />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="A particular car?"
              hint="Optional — brand alone is fine, any Maruti will match">
              <CarPicker
                catalog={catalog}
                brand={form.brand}
                model={form.model}
                onPick={(brand, model) => setForm((f) => ({ ...f, brand, model }))}
              />
            </Field>
          </div>
        </>
      )}

      <div className="mt-3">
        <Field label="What did they say?" hint="The bit worth remembering on the next call">
          <textarea rows={3} className={inputCls} value={form.note}
            onChange={(e) => set('note')(e.target.value)}
            placeholder={car
              ? 'e.g. asked us to hold it till Friday, coming with his brother'
              : 'e.g. wants white, automatic, will pay cash'} />
        </Field>
      </div>

      <button
        type="button"
        onClick={() => set('pinned')(!form.pinned)}
        className={`mt-3 inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold transition ${
          form.pinned
            ? 'border-violet-300 bg-violet-50 text-violet-700'
            : 'border-slate-200 bg-white text-slate-500 hover:border-violet-200'
        }`}
      >
        <FiCheck className={`h-3.5 w-3.5 ${form.pinned ? '' : 'opacity-30'}`} />
        Pin this one to the top
      </button>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy}>
          {editing ? 'Save enquiry' : 'Save enquiry'}
        </Button>
      </div>
    </Modal>
  );
};

// ── One call ─────────────────────────────────────────────────────────────────
const InquiryCard = ({ inquiry, busy, onCalled, onClose_, onReopen, onEdit, onDelete }) => {
  const matches = inquiry.matches || [];
  const ready = inquiry.status !== 'closed' && matches.length > 0;

  return (
    <div className={`rounded-2xl border bg-white p-4 shadow-sm transition ${
      ready ? 'border-emerald-300 ring-1 ring-emerald-100' : 'border-slate-200'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-base font-black text-slate-900">{inquiry.customer_name}</p>
            {inquiry.pinned && <Chip tone="open">Pinned</Chip>}
            <Chip tone={inquiry.status}>{inquiry.status_label}</Chip>
            {inquiry.source && (
              <Chip>{inquiry.source_label || SOURCE_LABELS[inquiry.source] || inquiry.source}</Chip>
            )}
          </div>
          <a href={`tel:${inquiry.customer_phone}`}
            className="mt-0.5 inline-flex items-center gap-1.5 text-xs font-bold text-violet-600 hover:underline">
            <FiPhone className="h-3 w-3" /> {inquiry.customer_phone}
          </a>
          <p className="mt-1 text-sm font-bold text-slate-700">
            {inquiry.is_waiting_for_stock
              ? `Wants ${inquiry.wanted || 'a car'}`
              : `Asked about ${inquiry.car_name}`}
            {inquiry.year_from ? ` · ${inquiry.year_from}+` : ''}
            {inquiry.budget_max ? ` · up to ${fmtMoney(inquiry.budget_max)}` : ''}
          </p>
          {inquiry.note && (
            <p className="mt-1 rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600">
              “{inquiry.note}”
            </p>
          )}
          <p className="mt-1.5 text-[11px] text-slate-400">
            {inquiry.taken_by_name ? `Taken by ${inquiry.taken_by_name} · ` : ''}
            {inquiry.created_at_display}
          </p>
        </div>

        <div className="flex shrink-0 gap-1">
          <button type="button" onClick={() => onEdit(inquiry)} title="Edit"
            className="rounded-lg p-2 text-slate-400 transition hover:bg-violet-50 hover:text-violet-700">
            <FiUser className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => onDelete(inquiry)} title="Remove"
            className="rounded-lg p-2 text-slate-300 transition hover:bg-rose-50 hover:text-rose-600">
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* The whole reason the call was written down */}
      {ready && (
        <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/70 p-3">
          <p className="text-[11px] font-black uppercase tracking-wide text-emerald-700">
            {matches.length === 1 ? 'This car is on the lot now' : `${matches.length} cars on the lot now`}
          </p>
          <div className="mt-1.5 space-y-1">
            {matches.map((m) => (
              <p key={m.id} className="text-sm font-bold text-emerald-900">
                {m.display_name}
                <span className="ml-1.5 text-xs font-semibold text-emerald-700/80">
                  {[m.year, m.registration_number, m.asking_price ? fmtMoney(m.asking_price) : '']
                    .filter(Boolean).join(' · ')}
                </span>
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
        {inquiry.status === 'closed' ? (
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => onReopen(inquiry)}>
            <FiRotateCcw className="mr-1.5 h-3.5 w-3.5" /> Re-open
          </Button>
        ) : (
          <>
            {inquiry.status === 'open' && (
              <Button size="sm" disabled={busy} onClick={() => onCalled(inquiry)}>
                <FiPhoneCall className="mr-1.5 h-3.5 w-3.5" /> Called back
              </Button>
            )}
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => onClose_(inquiry)}>
              <FiCheck className="mr-1.5 h-3.5 w-3.5" /> Close
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

// ── The book ─────────────────────────────────────────────────────────────────
const CarInquiries = ({ catalog = [] }) => {
  const [tab, setTab] = useState('open');
  const [query, setQuery] = useState('');
  const [data, setData] = useState({ inquiries: [], ready_to_call: 0, open_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await carsTeamApi.getInquiries({
        status: tab || undefined,
        q: query.trim() || undefined,
      });
      setData(res.data || { inquiries: [] });
    } catch (e) {
      setError(errText(e, 'Could not load the enquiries.'));
    } finally {
      setLoading(false);
    }
  }, [tab, query]);

  useEffect(() => { load(); }, [load]);

  const move = async (inquiry, patch) => {
    setBusyId(inquiry.id);
    try {
      await carsTeamApi.updateInquiry(inquiry.id, patch);
      load();
    } catch (e) {
      setError(errText(e, 'Could not update the enquiry.'));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (inquiry) => {
    setBusyId(inquiry.id);
    try {
      await carsTeamApi.removeInquiry(inquiry.id);
      load();
    } catch (e) {
      setError(errText(e, 'Could not remove the enquiry.'));
    } finally {
      setBusyId(null);
    }
  };

  // The ones with a car waiting come first — that is the day's work.
  const sorted = useMemo(() => {
    const rows = data.inquiries || [];
    const score = (r) => (r.status !== 'closed' && (r.matches || []).length ? 0 : 1);
    return [...rows].sort((a, b) => score(a) - score(b));
  }, [data]);

  return (
    <div className="space-y-4">
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {data.ready_to_call > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <FiPhoneCall className="h-4 w-4 text-emerald-700" />
          <p className="text-sm font-black text-emerald-800">
            {data.ready_to_call} {data.ready_to_call === 1 ? 'person is' : 'people are'} waiting
            for a car that is on the lot right now
          </p>
          <span className="text-xs font-semibold text-emerald-700/80">— ring them before someone else does.</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value)}
              className={`rounded-xl px-3.5 py-2 text-xs font-bold transition ${
                tab === t.value
                  ? 'bg-violet-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:text-slate-900'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative min-w-[12rem] flex-1">
          <FiSearch className={searchIconCls} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Name, phone or car…"
            className={searchInputCls}
          />
        </div>
        <button type="button" onClick={load}
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50">
          <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
          <FiPlus className="mr-1.5 h-4 w-4" /> Note a call
        </Button>
      </div>

      {loading ? (
        <p className="py-16 text-center text-sm text-slate-400">Loading…</p>
      ) : sorted.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center">
          <FiPhone className="mx-auto mb-2 h-8 w-8 text-slate-200" />
          <p className="text-sm font-bold text-slate-600">
            {tab === 'open' ? 'Nobody is waiting for a car' : 'Nothing here'}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Next time someone rings about a car we don't have, note it here and we ring them back.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {sorted.map((inquiry) => (
            <InquiryCard
              key={inquiry.id}
              inquiry={inquiry}
              busy={busyId === inquiry.id}
              onCalled={(i) => move(i, { status: 'contacted' })}
              onClose_={(i) => move(i, { status: 'closed' })}
              onReopen={(i) => move(i, { status: 'open' })}
              onEdit={(i) => { setEditing(i); setFormOpen(true); }}
              onDelete={remove}
            />
          ))}
        </div>
      )}

      <InquiryForm
        open={formOpen}
        initial={editing}
        catalog={catalog}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSaved={load}
      />
    </div>
  );
};

export default CarInquiries;
